import { CommentTextSchema } from '@cims-online/shared'
import { Button, Form } from 'antd'
import CIMSTextArea from 'components/CIMSTextArea'
import { useRef, useState } from 'react'
import { useAddWorkflowRecordCommentMutation } from 'schema'
import { DefaultMutationHookOptions } from 'utils/defaults'
import type { CommentFormProps } from '../../types'

function validationIssue(text: string): string | undefined {
  if (!text) return
  const parsed = CommentTextSchema.safeParse(text)
  return parsed.success ? undefined : parsed.error.issues[0]?.message
}

/** Adds a validated comment through the dedicated workflow-record mutation. */
export default function CommentForm({
  fieldId,
  name,
  onCommentAdded,
  recordId,
  rowCreate,
}: CommentFormProps) {
  const [text, setText] = useState(rowCreate.initialValue)
  const [issue, setIssue] = useState<string>()
  const submitting = useRef(false)
  const [submittingState, setSubmittingState] = useState(false)
  const [addComment, { loading }] = useAddWorkflowRecordCommentMutation(
    DefaultMutationHookOptions
  )
  const parsed = CommentTextSchema.safeParse(text)
  const mutationPending = loading || submittingState

  async function submit() {
    const submitted = CommentTextSchema.safeParse(text)
    if (!submitted.success) {
      setIssue(submitted.error.issues[0]?.message)
      return
    }
    if (submitting.current) return

    submitting.current = true
    setSubmittingState(true)
    try {
      const result = await addComment({
        variables: {
          input: { fieldId, recordId, text: submitted.data },
        },
      })
      if (!result.data?.addWorkflowRecordComment.edited) return

      setText(rowCreate.initialValue)
      setIssue(undefined)
      await onCommentAdded()
    } catch {
      // Default mutation and query options present request errors to the user.
    } finally {
      submitting.current = false
      setSubmittingState(false)
    }
  }

  return (
    <Form layout="vertical" onFinish={submit} style={{ width: '100%' }}>
      {!rowCreate.hideFieldValue && (
        <Form.Item help={issue} validateStatus={issue ? 'error' : undefined}>
          <CIMSTextArea
            aria-label={`Add a comment to ${name}`}
            disabled={rowCreate.readOnly}
            onBlur={() => setIssue(validationIssue(text))}
            onChange={({ target: { value } }) => {
              setText(value)
              setIssue(validationIssue(value))
            }}
            placeholder="Add a comment..."
            rows={1}
            style={{ width: '100%' }}
            value={text}
          />
        </Form.Item>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          disabled={!parsed.success || mutationPending}
          htmlType="submit"
          loading={mutationPending}
          type="primary"
        >
          Comment
        </Button>
      </div>
    </Form>
  )
}
